import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Button, LanguageSelector, LogoLoader } from '@unite-us/app-components';
import parseParams from './utils/parseParams';
import ErrorPage from './components/ErrorPage';
import Header from './components/Header';
import Spinner from './components/Spinner/Spinner';
import ProgramSearch from './components/ProgramSearch/ProgramSearch';
import ErrorPageNoResults from './components/ErrorPageNoResults';
import BackToTopButton from './components/BackToTopButton';
import useProgramsFromSearchApi from './hooks/useProgramsFromSearchApi';
import {
  isRichTextEmpty,
} from './hooks/utils/formatProgramInfo';
import formatAddress from './hooks/utils/formatAddress';
import {
  emailBackfill, hoursBackfill, phoneNumberBackfill, websiteBackfill,
} from './hooks/utils/programDataBackfill';
import print from './utils/print';

import './stylesheets/main.scss';

const DISPLAY_NAME = 'unite-us__shares-app';

function App({ onChangeLanguage, locale }) {
  const {
    groupIds,
    programIds,
    lat,
    lon,
    printOnLoad,
  } = parseParams();
  const {
    hint1246HideLgbtqPlusFilter,
  } = useFlags();

  // Check for validation errors first
  let errorMsg;
  if (groupIds?.length && programIds?.length) {
    errorMsg = 'Invalid Input Provided: Can only share Groups or Programs';
  } else if (!(groupIds?.length || programIds?.length)) {
    errorMsg = 'No Network or Programs provided';
  }

  // Pass dummy values when there are validation errors to prevent hook from throwing
  const {
    isLoading,
    data: programs = [],
    isError,
  } = useProgramsFromSearchApi(
    errorMsg ? ['dummy'] : (programIds || []),
    errorMsg ? [] : (groupIds || []),
    lat,
    lon,
    locale,
  );

  const { formatMessage } = useIntl();
  const intl = useIntl();

  useEffect(() => {
    if (printOnLoad && programs.length > 0 && !isLoading) {
      setTimeout(() => {
        print();
      }, 300);
    }
  }, [programs, isLoading, printOnLoad]);

  useEffect(() => {
    if (!isLoading && !isError && !groupIds?.length && programIds?.length !== programs.length) {
      const missing = new Set(programIds);
      programs.forEach((p) => {
        missing.delete(p.id);
      });
      window.DD_RUM?.addError(new Error(`Search failed to return programIds: ${Array.from(missing).toString()}`));
    }
  }, [groupIds, programs, programIds, isLoading, isError]);

  // Check for validation errors after all hooks are called
  if (errorMsg) {
    return (
      <main className="app">
        <Header />
        <div className="error">
          <ErrorPage
            error={{ response: { status: 404 } }}
            message={errorMsg}
          />
        </div>
      </main>
    );
  }

  return (
    <main className={DISPLAY_NAME}>
      <Header />
      {isError && (
        <p>
          <FormattedMessage
            id="App.programFetchingError"
            defaultMessage="There was an error fetching the requested programs"
          />
        </p>
      )}
      {isLoading && (
        <div className="flex flex-col items-center justify-center h-screen">
          {hint1246HideLgbtqPlusFilter ? (
            <Spinner
              text={formatMessage({
                id: 'App.isLoading',
                defaultMessage: 'Loading...',
              })}
            />
          ) : (
            <LogoLoader
              description={formatMessage({
                id: 'App.isLoading',
                defaultMessage: 'Loading...',
              })}
              height={160}
              width={160}
            />
          )}
        </div>
      )}
      {!isError && !isLoading && (
        <>
          <div className={`${DISPLAY_NAME}--resources-toolbar`}>
            <h1>
              <FormattedMessage
                id="App.h1"
                defaultMessage="Resources for you"
              />
            </h1>
            {/* TODO: change this label to Download on mobile */}
            <Button
              primary
              label={formatMessage({
                id: 'App.Button.download',
                defaultMessage: 'Download',
              })}
              className="print-btn share-print-button--mobile"
              onClick={() => print()}
            />
            <Button
              primary
              label={formatMessage({
                id: 'App.Button.print',
                defaultMessage: 'Print',
              })}
              className="print-btn share-print-button"
              onClick={() => print()}
            />
            <div className="lang-selector">
              <LanguageSelector
                onChange={onChangeLanguage}
                field={{ value: locale }}
                appContext="shares"
                displayLabelInSelectedLanguage
                theme={{ width: 'unset', fontSize: '0.875rem', marginTop: 0 }}
                intlProvider={intl}
              />
            </div>
          </div>
          <div className="card-container col-span-12">
            {!isLoading && programs.length < 1 && (
              <div className="error">
                <ErrorPageNoResults />
              </div>
            )}
            {!isLoading && programs.map(({
              accessibility_options,
              age_requirements,
              age_specializations,
              city_requirements,
              county_requirements,
              delivery_options,
              description,
              distance,
              eligibility_text,
              email_addresses,
              fee_schedule_program,
              gender_requirements,
              id,
              is_active,
              languages,
              locations,
              name,
              name_untranslated,
              payment_options,
              phone_numbers,
              population_restrictions,
              population_specializations,
              postal_code_requirements,
              provider,
              state_requirements,
              transportation_options,
              website_url,
              service_areas,
              offers_specialized_screening,
            }) => {
              const location = locations.find(({ is_closest }) => is_closest);
              const address = formatAddress(location || {});
              const programDescription = isRichTextEmpty(description);
              const phoneNumber = phoneNumberBackfill(location, phone_numbers, provider);
              const email = emailBackfill(location, email_addresses, provider);
              const website = websiteBackfill(website_url, provider);
              const hours = hoursBackfill(location, provider);

              return (
                <ProgramSearch
                  accessibility_options={accessibility_options}
                  address={address}
                  age_requirements={age_requirements}
                  age_specializations={age_specializations}
                  city_requirements={city_requirements}
                  county_requirements={county_requirements}
                  delivery_options={delivery_options}
                  description={programDescription}
                  distance={location ? distance : null}
                  eligibilityText={eligibility_text}
                  email={email}
                  feeScheduleProgram={fee_schedule_program}
                  gender_requirements={gender_requirements}
                  hours={hours}
                  key={id}
                  languages={languages}
                  name={name}
                  name_untranslated={name_untranslated}
                  payment_options={payment_options}
                  phone={phoneNumber}
                  population_restrictions={population_restrictions}
                  population_specializations={population_specializations}
                  postal_code_requirements={postal_code_requirements}
                  providerName={provider.name}
                  providerNameUntranslated={provider.name_untranslated}
                  state_requirements={state_requirements}
                  transportation_options={transportation_options}
                  website={website}
                  service_areas={service_areas}
                  offers_specialized_screening={offers_specialized_screening}
                  is_active={is_active}
                />
              );
            })}
            <BackToTopButton />
          </div>
        </>
      )}
    </main>
  );
}

App.propTypes = {
  onChangeLanguage: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
};

export default App;
